import React, { useState } from "react";
import { SENSAIPageContent, SENSAIPageContext } from "../types";

interface TeachTabProps {
  content: SENSAIPageContent | null;
  context: SENSAIPageContext;
}

export const TeachTab: React.FC<TeachTabProps> = ({ content, context }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [tourMode, setTourMode] = useState(false);

  if (!content) {
    return (
      <div className="text-center text-slate-400">
        <div className="mb-4">📚</div>
        <div className="text-sm">No teaching content available for this page yet.</div>
        {context.userRole === "admin" && (
          <button className="mt-3 px-3 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700">
            Add Content
          </button>
        )}
      </div>
    );
  }

  const steps = content.teach.steps;

  const handleStartTour = () => {
    setTourMode(true);
    setCurrentStep(0);
  };

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setTourMode(false);
      setCurrentStep(0);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const highlightElement = (target: string) => {
    const element = document.querySelector(target);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.classList.add('sensai-highlight');
      setTimeout(() => {
        element.classList.remove('sensai-highlight');
      }, 3000);
    }
  };

  if (tourMode && steps.length > 0) {
    const step = steps[currentStep];
    
    if (step.target) {
      highlightElement(step.target);
    }

    return (
      <div className="space-y-4">
        <div className="sensai-card p-5 rounded-xl">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-slate-400 font-medium">
              Step {currentStep + 1} of {steps.length}
            </div>
            <button
              onClick={() => setTourMode(false)}
              className="text-xs text-slate-400 hover:text-white transition-colors"
            >
              Exit Tour
            </button>
          </div>
          <h3 className="font-bold text-white mb-3 text-lg">{step.title}</h3>
          <p className="text-slate-300 text-sm mb-4 leading-relaxed">{step.tip}</p>
          {step.hotkey && (
            <div className="text-xs text-slate-400 mb-4">
              Hotkey: <kbd className="bg-slate-700/50 px-2 py-1 rounded-md font-mono">{step.hotkey}</kbd>
            </div>
          )}
          <div className="flex justify-between">
            <button
              onClick={handlePrevStep}
              disabled={currentStep === 0}
              className="px-4 py-2 bg-slate-700/50 text-white text-sm rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600/50 transition-colors font-medium"
            >
              Previous
            </button>
            <button
              onClick={handleNextStep}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white text-sm rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all font-medium"
            >
              {currentStep === steps.length - 1 ? "Finish" : "Next"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-slate-300 text-sm mb-4">
        {content.teach.intro}
      </div>

      <button
        onClick={handleStartTour}
        className="w-full px-5 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all font-semibold"
        disabled={steps.length === 0}
      >
        🎯 Start Guided Tour
      </button>

      <div className="space-y-4">
        <h3 className="font-bold text-white text-base">Quick Tips</h3>
        {steps.map((step, index) => (
          <div
            key={step.id}
            className="sensai-card p-4 rounded-xl cursor-pointer"
            onClick={() => step.target && highlightElement(step.target)}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="font-semibold text-white text-sm mb-2">{step.title}</h4>
                <p className="text-slate-300 text-xs leading-relaxed">{step.tip}</p>
                {step.hotkey && (
                  <div className="text-xs text-slate-400 mt-3">
                    <kbd className="bg-slate-700/50 px-2 py-1 rounded-md font-mono">{step.hotkey}</kbd>
                  </div>
                )}
              </div>
              {step.target && (
                <div className="text-purple-400 text-sm ml-3">👁️</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
